import {
  type DebugEntry,
  type DebugGroup,
  DEBUG_GROUP_LABELS,
  getDebugEntriesForGroup,
  getDebugEntryCount,
  getDefaultDebugGroup,
  getOrderedDebugGroups,
  stringifyDebugPayload,
} from './session-debug-model';

type SessionDebugPanelOptions = {
  entries: DebugEntry[];
  onClearGroup: (group: DebugGroup) => void;
};

type SessionDebugPanelController = {
  refresh: () => void;
};

let panel: HTMLDivElement | null = null;
let toolbarButton: HTMLButtonElement | null = null;
let tabsEl: HTMLDivElement | null = null;
let bodyEl: HTMLDivElement | null = null;
let copyButton: HTMLButtonElement | null = null;
let copyFiveMinuteButton: HTMLButtonElement | null = null;
let copyTenMinuteButton: HTMLButtonElement | null = null;
let clearButton: HTMLButtonElement | null = null;
let activeGroup: DebugGroup = 'connection-efficiency';
let isOpen = false;
let optionsRef: SessionDebugPanelOptions | null = null;
const copyStatusTimers = new WeakMap<HTMLButtonElement, number>();

function copyDebugText(text: string): boolean {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

function setCopyStatus(button: HTMLButtonElement | null, text: string, originalText: string): void {
  if (!button) return;
  const existingTimer = copyStatusTimers.get(button);
  if (existingTimer) window.clearTimeout(existingTimer);
  button.textContent = text;
  const timer = window.setTimeout(() => {
    button.textContent = originalText;
    copyStatusTimers.delete(button);
  }, 1200);
  copyStatusTimers.set(button, timer);
}

function copyEntries(button: HTMLButtonElement | null, entries: DebugEntry[], originalText: string): void {
  if (!button) return;
  const text = entries.map((entry) => entry.text).join('\n');
  if (!text) return;
  const done = navigator.clipboard && typeof navigator.clipboard.writeText === 'function'
    ? navigator.clipboard.writeText(text).then(() => true).catch(() => copyDebugText(text))
    : Promise.resolve(copyDebugText(text));
  done.then((ok) => setCopyStatus(button, ok ? '已复制' : '失败', originalText));
}

function getActiveGroupEntries(): DebugEntry[] {
  return optionsRef ? getDebugEntriesForGroup(optionsRef.entries, activeGroup) : [];
}

function getRecentEntries(entries: DebugEntry[], minutes: number): DebugEntry[] {
  const cutoff = Date.now() - minutes * 60 * 1000;
  return entries.filter((entry) => {
    const time = new Date(entry.time).getTime();
    return Number.isFinite(time) && time >= cutoff;
  });
}

function copyActiveGroup(): void {
  copyEntries(copyButton, getActiveGroupEntries(), '复制本组');
}

function copyActiveGroupRecent(minutes: number, button: HTMLButtonElement | null, originalText: string): void {
  copyEntries(button, getRecentEntries(getActiveGroupEntries(), minutes), originalText);
}

function ensureActiveGroup(): void {
  if (!optionsRef) return;
  const groups = getOrderedDebugGroups(optionsRef.entries);
  if (!groups.includes(activeGroup)) {
    activeGroup = getDefaultDebugGroup(optionsRef.entries);
  }
}

function renderTabs(): void {
  if (!tabsEl || !optionsRef) return;
  tabsEl.innerHTML = '';
  const groups = getOrderedDebugGroups(optionsRef.entries);
  for (const group of groups) {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'session-debug-tab';
    tab.dataset.group = group;
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-selected', group === activeGroup ? 'true' : 'false');
    tab.textContent = `${DEBUG_GROUP_LABELS[group] || group} (${getDebugEntryCount(optionsRef.entries, group)})`;
    if (group === activeGroup) tab.classList.add('active');
    tab.addEventListener('click', () => {
      activeGroup = group;
      renderPanel();
    });
    tabsEl.appendChild(tab);
  }
}

function renderBody(): void {
  if (!bodyEl || !optionsRef) return;
  bodyEl.innerHTML = '';
  const groupEntries = getActiveGroupEntries();
  for (const entry of groupEntries) {
    const row = document.createElement('div');
    row.className = 'session-debug-log-row';

    const meta = document.createElement('div');
    meta.className = 'session-debug-log-meta';

    const time = document.createElement('span');
    time.className = 'session-debug-log-time';
    time.textContent = formatDebugTime(entry.time);

    const event = document.createElement('span');
    event.className = 'session-debug-log-event';
    event.textContent = entry.event;

    meta.appendChild(time);
    meta.appendChild(event);

    const payload = document.createElement('div');
    payload.className = 'session-debug-log-payload';
    payload.textContent = stringifyDebugPayload(entry.payload);

    row.appendChild(meta);
    row.appendChild(payload);
    bodyEl.appendChild(row);
  }
  const hasEntries = groupEntries.length > 0;
  if (copyButton) copyButton.disabled = !hasEntries;
  if (copyFiveMinuteButton) copyFiveMinuteButton.disabled = getRecentEntries(groupEntries, 5).length === 0;
  if (copyTenMinuteButton) copyTenMinuteButton.disabled = getRecentEntries(groupEntries, 10).length === 0;
  if (clearButton) clearButton.disabled = !hasEntries;
  if (!hasEntries) {
    const empty = document.createElement('div');
    empty.className = 'session-debug-empty';
    empty.textContent = '本组暂无调试信息';
    bodyEl.appendChild(empty);
  }
  if (isOpen) bodyEl.scrollTop = bodyEl.scrollHeight;
}

function formatDebugTime(value: string): string {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return value;
  const date = new Date(time);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function renderButton(): void {
  if (!toolbarButton || !optionsRef) return;
  toolbarButton.textContent = '调试面板';
  toolbarButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  toolbarButton.classList.toggle('active', isOpen);
}

function renderPanel(): void {
  ensureActiveGroup();
  renderButton();
  if (!panel) return;
  panel.hidden = !isOpen;
  if (!isOpen) return;
  renderTabs();
  renderBody();
}

function createToolbarButton(toolbar: Element): void {
  if (toolbarButton) return;
  toolbarButton = document.createElement('button');
  toolbarButton.type = 'button';
  toolbarButton.id = 'session-debug-toggle';
  toolbarButton.className = 'input-toolbar-btn session-debug-toolbar-btn';
  toolbarButton.textContent = '调试面板';
  toolbarButton.title = '打开调试面板';
  toolbarButton.setAttribute('aria-controls', 'session-debug-panel');
  toolbarButton.setAttribute('aria-expanded', 'false');
  toolbarButton.addEventListener('click', () => {
    isOpen = !isOpen;
    renderPanel();
  });
  toolbar.appendChild(toolbarButton);
}

function createPanel(toolbar: Element): void {
  if (panel) return;
  panel = document.createElement('div');
  panel.id = 'session-debug-panel';
  panel.className = 'session-debug-panel';
  panel.hidden = true;

  const header = document.createElement('div');
  header.className = 'session-debug-header';

  const title = document.createElement('div');
  title.className = 'session-debug-title';
  title.textContent = '调试面板';

  copyButton = document.createElement('button');
  copyButton.type = 'button';
  copyButton.className = 'session-debug-action';
  copyButton.textContent = '复制本组';
  copyButton.addEventListener('click', copyActiveGroup);

  copyFiveMinuteButton = document.createElement('button');
  copyFiveMinuteButton.type = 'button';
  copyFiveMinuteButton.className = 'session-debug-action';
  copyFiveMinuteButton.textContent = '复制5分钟';
  copyFiveMinuteButton.addEventListener('click', () => copyActiveGroupRecent(5, copyFiveMinuteButton, '复制5分钟'));

  copyTenMinuteButton = document.createElement('button');
  copyTenMinuteButton.type = 'button';
  copyTenMinuteButton.className = 'session-debug-action';
  copyTenMinuteButton.textContent = '复制10分钟';
  copyTenMinuteButton.addEventListener('click', () => copyActiveGroupRecent(10, copyTenMinuteButton, '复制10分钟'));

  clearButton = document.createElement('button');
  clearButton.type = 'button';
  clearButton.className = 'session-debug-action';
  clearButton.textContent = '清空本组';
  clearButton.addEventListener('click', () => {
    if (!optionsRef) return;
    optionsRef.onClearGroup(activeGroup);
    activeGroup = getDefaultDebugGroup(optionsRef.entries);
    renderPanel();
  });

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'session-debug-action session-debug-close';
  closeButton.textContent = '关闭';
  closeButton.setAttribute('aria-label', '关闭调试窗口');
  closeButton.addEventListener('click', () => {
    isOpen = false;
    renderPanel();
  });

  header.appendChild(title);
  header.appendChild(copyButton);
  header.appendChild(copyFiveMinuteButton);
  header.appendChild(copyTenMinuteButton);
  header.appendChild(clearButton);
  header.appendChild(closeButton);

  const tabsWrap = document.createElement('div');
  tabsWrap.className = 'session-debug-tabs-wrap';

  const tabsLabel = document.createElement('span');
  tabsLabel.className = 'session-debug-tabs-label';
  tabsLabel.textContent = '分组：';

  tabsEl = document.createElement('div');
  tabsEl.className = 'session-debug-tabs';
  tabsEl.setAttribute('role', 'tablist');
  tabsWrap.appendChild(tabsLabel);
  tabsWrap.appendChild(tabsEl);

  bodyEl = document.createElement('div');
  bodyEl.className = 'session-debug-body';
  bodyEl.setAttribute('role', 'tabpanel');

  panel.appendChild(header);
  panel.appendChild(tabsWrap);
  panel.appendChild(bodyEl);
  toolbar.parentElement?.insertBefore(panel, toolbar);
}

export function bindSessionDebugPanel(options: SessionDebugPanelOptions): SessionDebugPanelController {
  optionsRef = options;
  if (typeof document !== 'undefined') {
    const toolbar = document.querySelector('.input-toolbar');
    if (toolbar) {
      createToolbarButton(toolbar);
      createPanel(toolbar);
    }
  }
  renderPanel();
  return { refresh: renderPanel };
}
